import {
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
  useMemo,
} from 'react';
import { cva, cx, VariantProps } from 'class-variance-authority';
import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';
import { pipe } from 'fp-ts/lib/function';
import { fromPredicate, getOrElseW, map } from 'fp-ts/lib/Option';
import { filter } from 'fp-ts/Array';
import { RadioGroupExpanderInputProps } from './Expander';
import { Label } from '../Label';

const root = cva(['grid', 'grid-cols-1', 'gap-4', 'md:gap-6', 'mt-4'], {
  variants: {
    direction: {
      row: ['sm:grid-cols-2'],
      col: [],
    },
  },
  defaultVariants: {
    direction: 'row',
  },
});

type RadioGroupExpanderRootTypeMap = BaseTypeMap<
  'div',
  {
    label: string;
    required?: boolean;
    name: string;
    value?: string | ReadonlyArray<string> | number | undefined;
    onChange?: RadioGroupExpanderInputProps['onChange'];
    children: [
      ReactElement<RadioGroupExpanderInputProps>,
      ReactElement<RadioGroupExpanderInputProps>
    ];
  } & VariantProps<typeof root>
>;

export type RadioGroupExpanderRootProps =
  ComponentProps<RadioGroupExpanderRootTypeMap>;

export const RadioGroupExpanderRoot: Component<RadioGroupExpanderRootTypeMap> =
  forwardRef(function RadioGroupExpanderRoot(
    {
      children = [],
      className,
      label,
      value,
      name,
      onChange,
      direction = 'row',
      required,
      ...rest
    },
    ref
  ) {
    const [left, right] = children;
    const content = useMemo(
      () =>
        pipe(
          [
            pipe(
              left,
              fromPredicate(isValidElement),
              map((l) =>
                cloneElement(l, {
                  className: 'sm:col-start-1',
                  checked: l.props.value === value,
                  name,
                  id: `${name}-left`,
                  onChange,
                  key: 'left',
                })
              ),
              getOrElseW(() => undefined)
            ),
            pipe(
              right,
              fromPredicate(isValidElement),
              map((r) =>
                cloneElement(r, {
                  className: `sm:col-start-${direction === 'row' ? '2' : '1'}`,
                  checked: r.props.value === value,
                  name,
                  id: `${name}-right`,
                  onChange,
                  key: 'right',
                })
              ),
              getOrElseW(() => undefined)
            ),
          ],
          filter(Boolean)
        ),
      [left, right, value, name, onChange, direction]
    );

    return (
      <Box ref={ref} className={cx(className)} {...rest}>
        <Label>{!required ? `${label}` : `${label} *`}</Label>
        <Box className={cx(root({ direction }))}>{content}</Box>
      </Box>
    );
  });
